import { openChatCommand } from '../ai-chat/lib/chatCommandHandoff.js';
import { commandRequest } from '../../agents/context/ui-command-registry.js';
import { Fragment, useEffect, useState, useMemo, useRef, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Kanban,
  User,
  ListTodo,
  Calendar,
  Sparkles,
  FileText,
  LayoutDashboard,
  Settings,
  Plus,
  Globe,
  ArrowRight,
  Zap,
  Building2,
  Folder,
} from 'lucide-react';
import { Modal } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { i18n } from '../../i18n/index.js';
import { useI18nSubscription } from '../i18n/useI18nSubscription.js';
import { useCommandSearch } from './hooks/useCommandSearch.js';

interface CommandItem {
  id: string;
  title: string;
  subtitle?: string;
  category: 'nav' | 'action' | 'ai' | 'entity';
  icon: ReactElement;
  onSelect: () => void;
  keywords?: string[];
}

interface GlobalCommandMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateDeal?: () => void;
  onCreateContact?: () => void;
  onCreateTask?: () => void;
}

export function GlobalCommandMenu({
  isOpen,
  onClose,
  onCreateDeal,
  onCreateContact,
  onCreateTask,
}: GlobalCommandMenuProps): ReactElement {
  // Подписка на смену locale: пользователь переключает язык соответствующей
  // командой при открытом меню — перерисовка нужна без перезагрузки страницы.
  useI18nSubscription();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const commandSearch = useCommandSearch(search);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const items = useMemo<CommandItem[]>(() => {
    const list: CommandItem[] = [
      // Navigation
      {
        id: 'nav-deals',
        title: t('crm.nav.deals'),
        subtitle: t('crm.command_menu.goto_deals'),
        category: 'nav',
        icon: <Kanban size={15} className="text-amber-600" />,
        onSelect: () => {
          navigate('/deals');
          onClose();
        },
        keywords: ['deals', 'сделки', 'воронка', 'kanban', 'канбан', 'pipeline'],
      },
      {
        id: 'nav-contacts',
        title: t('crm.nav.contacts'),
        subtitle: t('crm.command_menu.goto_contacts'),
        category: 'nav',
        icon: <User size={15} className="text-blue-600" />,
        onSelect: () => {
          navigate('/contacts');
          onClose();
        },
        keywords: ['contacts', 'контакты', 'клиенты', 'база', 'people'],
      },
      {
        id: 'nav-tasks',
        title: t('crm.nav.tasks'),
        subtitle: t('crm.command_menu.goto_tasks'),
        category: 'nav',
        icon: <ListTodo size={15} className="text-emerald-600" />,
        onSelect: () => {
          navigate('/tasks');
          onClose();
        },
        keywords: ['tasks', 'задачи', 'дела', 'todo'],
      },
      {
        id: 'nav-projects',
        title: t('crm.nav.projects'),
        subtitle: t('crm.command_menu.goto_projects'),
        category: 'nav',
        icon: <Folder size={15} className="text-teal-600" />,
        onSelect: () => {
          navigate('/projects');
          onClose();
        },
        keywords: ['projects', 'проекты', 'проект'],
      },
      {
        id: 'nav-companies',
        title: t('crm.nav.companies'),
        subtitle: t('crm.command_menu.goto_companies'),
        category: 'nav',
        icon: <Building2 size={15} className="text-stone-600" />,
        onSelect: () => {
          navigate('/companies');
          onClose();
        },
        keywords: ['companies', 'компании', 'организации', 'фирмы'],
      },
      {
        id: 'nav-meetings',
        title: t('crm.nav.meetings'),
        subtitle: t('crm.command_menu.goto_meetings'),
        category: 'nav',
        icon: <Calendar size={15} className="text-blue-600" />,
        onSelect: () => {
          navigate('/meetings');
          onClose();
        },
        keywords: ['meetings', 'встречи', 'календарь', 'звонки'],
      },
      {
        id: 'nav-chat',
        title: t('crm.nav.chat'),
        subtitle: t('crm.command_menu.goto_chat'),
        category: 'nav',
        icon: <Sparkles size={15} className="text-amber-500" />,
        onSelect: () => {
          navigate('/chat');
          onClose();
        },
        keywords: ['chat', 'чат', 'ai', 'ассистент', 'помощник', 'govardos'],
      },
      {
        id: 'nav-templates',
        title: t('crm.nav.templates'),
        subtitle: t('crm.command_menu.goto_templates'),
        category: 'nav',
        icon: <FileText size={15} className="text-stone-600" />,
        onSelect: () => {
          navigate('/templates');
          onClose();
        },
        keywords: ['templates', 'шаблоны', 'письма', 'документы'],
      },
      {
        id: 'nav-dashboard',
        title: t('crm.nav.dashboard'),
        subtitle: t('crm.command_menu.goto_dashboard'),
        category: 'nav',
        icon: <LayoutDashboard size={15} className="text-teal-600" />,
        onSelect: () => {
          navigate('/dashboard');
          onClose();
        },
        keywords: ['dashboard', 'дашборд', 'сводка', 'метрики', 'аналитика'],
      },
      {
        id: 'nav-settings',
        title: t('crm.nav.settings'),
        subtitle: t('crm.command_menu.goto_settings'),
        category: 'nav',
        icon: <Settings size={15} className="text-stone-500" />,
        onSelect: () => {
          navigate('/settings');
          onClose();
        },
        keywords: ['settings', 'настройки', 'профиль', 'аккаунт'],
      },

      // Quick Actions
      {
        id: 'act-create-deal',
        title: t('crm.command_menu.create_deal'),
        subtitle: t('crm.command_menu.create_deal_desc'),
        category: 'action',
        icon: <Plus size={15} className="text-amber-600" />,
        onSelect: () => {
          if (onCreateDeal) {
            onCreateDeal();
          } else {
            navigate('/deals?create=true');
          }
          onClose();
        },
        keywords: ['new deal', 'новая сделка', 'создать сделку', 'add deal'],
      },
      {
        id: 'act-create-contact',
        title: t('crm.command_menu.create_contact'),
        subtitle: t('crm.command_menu.create_contact_desc'),
        category: 'action',
        icon: <Plus size={15} className="text-blue-600" />,
        onSelect: () => {
          if (onCreateContact) {
            onCreateContact();
          } else {
            navigate('/contacts?create=true');
          }
          onClose();
        },
        keywords: ['new contact', 'новый контакт', 'создать контакт', 'add contact'],
      },
      {
        id: 'act-create-task',
        title: t('crm.command_menu.create_task'),
        subtitle: t('crm.command_menu.create_task_desc'),
        category: 'action',
        icon: <Plus size={15} className="text-emerald-600" />,
        onSelect: () => {
          if (onCreateTask) {
            onCreateTask();
          } else {
            navigate('/tasks?create=true');
          }
          onClose();
        },
        keywords: ['new task', 'новая задача', 'создать задачу', 'add task', 'todo'],
      },
      {
        id: 'act-switch-lang',
        title: t('crm.command_menu.switch_language'),
        subtitle: i18n.locale === 'ru' ? 'Switch to English' : 'Переключить на русский',
        category: 'action',
        icon: <Globe size={15} className="text-amber-600" />,
        onSelect: () => {
          i18n.setLocale(i18n.locale === 'ru' ? 'en' : 'ru');
          onClose();
        },
        // The search index carries both scripts so the command matches in
        // either locale: latin "russian" in EN mode, cyrillic «английский» in
        // RU mode. The displayed subtitle stays localized as before.
        keywords: [
          'language',
          'язык',
          'english',
          'russian',
          'английский',
          'русский',
          'locale',
          'translate',
          'переключить',
        ],
      },

      // AI Scenarios
      {
        id: 'ai-hot-leads',
        title: t('crm.ai_chat.suggestions.hot_leads'),
        subtitle: t('crm.command_menu.hot_leads_desc'),
        category: 'ai',
        icon: <Zap size={15} className="text-amber-600" />,
        onSelect: () => {
          openChatCommand(navigate, commandRequest('scenarioHotLeads'));
          onClose();
        },
        keywords: ['hot leads', 'горячие лиды', 'лиды', 'лид', 'срочно'],
      },
      {
        id: 'ai-risk-audit',
        title: t('crm.ai_chat.suggestions.risk_audit'),
        subtitle: t('crm.command_menu.risk_audit_desc'),
        category: 'ai',
        icon: <Zap size={15} className="text-orange-600" />,
        onSelect: () => {
          openChatCommand(navigate, commandRequest('scenarioDealRisks'));
          onClose();
        },
        keywords: ['risk', 'риски', 'аудит', 'зависшие', 'проблемы'],
      },
      {
        id: 'ai-follow-up',
        title: t('crm.ai_chat.suggestions.follow_up'),
        subtitle: t('crm.command_menu.follow_up_desc'),
        category: 'ai',
        icon: <Zap size={15} className="text-blue-600" />,
        onSelect: () => {
          openChatCommand(navigate, commandRequest('scenarioFollowUp'));
          onClose();
        },
        keywords: ['follow up', 'письмо', 'email', 'фоллоу ап', 'черновик'],
      },
    ];

    return list;
  }, [t, navigate, onClose, onCreateDeal, onCreateContact, onCreateTask]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;

    return items.filter((item) => {
      const matchTitle = item.title.toLowerCase().includes(q);
      const matchSub = item.subtitle ? item.subtitle.toLowerCase().includes(q) : false;
      const matchKeywords = item.keywords
        ? item.keywords.some((k) => k.toLowerCase().includes(q))
        : false;
      return matchTitle || matchSub || matchKeywords;
    });
  }, [items, search]);

  // Результаты живого поиска по сущностям. Subtitle собирается из «лейбл
  // сущности · контекст»: контекст — то, что помогает отличить одноимённые
  // записи (компания сделки, email контакта, отрасль, привязка задачи).
  const entityItems = useMemo<CommandItem[]>(() => {
    if (commandSearch.query.length === 0) return [];
    const withContext = (
      id: string,
      kind: string,
      label: string,
      context: string | null,
      title: string,
      icon: ReactElement,
      route: string,
    ): CommandItem => ({
      id: `${kind}-${id}`,
      title,
      subtitle: context ? `${label} · ${context}` : label,
      category: 'entity',
      icon,
      onSelect: () => {
        navigate(route);
        onClose();
      },
    });

    const dealItems: CommandItem[] = commandSearch.data.deals.map((deal) =>
      withContext(
        deal.id,
        'deal',
        t('crm.command_menu.entity_deal'),
        deal.company?.name ??
          (deal.contact ? `${deal.contact.firstName} ${deal.contact.lastName ?? ''}`.trim() : null),
        deal.title,
        <Kanban size={15} className="text-amber-600" />,
        `/deals/${deal.id}`,
      ),
    );

    const contactItems: CommandItem[] = commandSearch.data.contacts.map((contact) =>
      withContext(
        contact.id,
        'contact',
        t('crm.command_menu.entity_contact'),
        contact.email ?? contact.companyRef?.name ?? null,
        contact.fullName,
        <User size={15} className="text-blue-600" />,
        `/contacts/${contact.id}`,
      ),
    );

    const companyItems: CommandItem[] = commandSearch.data.companies.map((company) =>
      withContext(
        company.id,
        'company',
        t('crm.command_menu.entity_company'),
        company.industry ?? null,
        company.name,
        <Building2 size={15} className="text-stone-600" />,
        `/companies/${company.id}`,
      ),
    );

    const taskItems: CommandItem[] = commandSearch.data.tasks.map((task) =>
      withContext(
        task.id,
        'task',
        t('crm.command_menu.entity_task'),
        task.deal?.title ??
          task.project?.name ??
          (task.contact ? `${task.contact.firstName} ${task.contact.lastName ?? ''}`.trim() : null),
        task.title,
        <ListTodo size={15} className="text-emerald-600" />,
        `/tasks/${task.id}`,
      ),
    );

    const projectItems: CommandItem[] = commandSearch.data.projects.map((project) =>
      withContext(
        project.id,
        'project',
        t('crm.command_menu.entity_project'),
        project.description?.trim() || null,
        project.name,
        <Folder size={15} className="text-teal-600" />,
        `/projects/${project.id}`,
      ),
    );

    return [...dealItems, ...contactItems, ...companyItems, ...taskItems, ...projectItems];
  }, [commandSearch.query, commandSearch.data, t, navigate, onClose]);

  // Клавиатура ходит по одному плоскому списку: сначала найденные сущности,
  // затем команды. Скролл к выбранному пункту держим в поле зрения.
  const combinedItems = useMemo(
    () => [...entityItems, ...filteredItems],
    [entityItems, filteredItems],
  );

  // Результаты приходят асинхронно: индекс может выйти за границы списка.
  useEffect(() => {
    if (selectedIndex >= combinedItems.length && combinedItems.length > 0) {
      setSelectedIndex(0);
    }
  }, [combinedItems.length, selectedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, combinedItems.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(
        (prev) => (prev - 1 + combinedItems.length) % Math.max(1, combinedItems.length),
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (combinedItems[selectedIndex]) {
        combinedItems[selectedIndex]?.onSelect();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Выбранная стрелками строка обязана оставаться видимой: список длиннее
  // своего max-height, и без этого Enter уходил бы в невидимую зону.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={560}
      styles={{
        body: {
          padding: 0,
          borderRadius: '16px',
          overflow: 'hidden',
          backgroundColor: 'var(--ant-color-bg-elevated)',
        },
      }}
    >
      <div className="flex flex-col" onKeyDown={handleKeyDown}>
        {/* Search header input */}
        <div className="flex items-center gap-3 border-b border-stone-200/80 px-4 py-3.5 bg-white">
          <Search size={18} className="text-amber-600 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={combinedItems.length > 0}
            aria-controls="global-command-menu-listbox"
            aria-autocomplete="list"
            aria-activedescendant={
              combinedItems[selectedIndex]
                ? `command-option-${combinedItems[selectedIndex]?.id}`
                : undefined
            }
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder={t('crm.command_menu.search_placeholder')}
            className="flex-1 bg-transparent text-sm text-stone-900 placeholder:text-stone-400 outline-none"
            data-testid="global-command-palette-input"
          />
          <kbd className="rounded border border-stone-200 bg-stone-100 px-1.5 py-0.5 font-mono text-[10px] text-stone-600">
            ESC
          </kbd>
        </div>

        {/* List of results: entity search hits first, then commands */}
        <div
          ref={listRef}
          id="global-command-menu-listbox"
          role="listbox"
          aria-label={t('crm.command_menu.search_placeholder')}
          className="max-h-[380px] overflow-y-auto p-2 crm-microscroll"
        >
          {combinedItems.length === 0 ? (
            <div className="py-8 text-center text-xs text-stone-400">
              {commandSearch.isSearching
                ? t('crm.command_menu.searching')
                : t('crm.command_menu.no_results')}
            </div>
          ) : (
            combinedItems.map((item, idx) => {
              // Hover selection follows real pointer movement only (mousemove):
              // Chromium synthesizes a mouseenter when the palette renders under
              // a stationary cursor, which used to steal the default highlight
              // from the first item right after opening (acceptance #837).
              const isSelected = idx === selectedIndex;
              return (
                <Fragment key={item.id}>
                  {idx === 0 && entityItems.length > 0 ? (
                    <div
                      aria-hidden="true"
                      className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-stone-400"
                    >
                      {t('crm.command_menu.search_results')}
                    </div>
                  ) : null}
                  {idx === entityItems.length && entityItems.length > 0 ? (
                    <div
                      aria-hidden="true"
                      className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-stone-400"
                    >
                      {t('crm.command_menu.commands')}
                    </div>
                  ) : null}
                  <div
                    role="option"
                    aria-selected={isSelected}
                    id={`command-option-${item.id}`}
                    onClick={() => item.onSelect()}
                    onMouseMove={() => setSelectedIndex(idx)}
                    data-testid={`command-item-${item.id}`}
                    className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3 py-2 text-xs transition-all ${
                      isSelected
                        ? 'bg-amber-100/80 text-amber-950 font-medium shadow-2xs'
                        : 'text-stone-700 hover:bg-stone-100'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-stone-50 border border-stone-200/60 shrink-0">
                        {item.icon}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-stone-900">{item.title}</p>
                        {item.subtitle && (
                          <p className="truncate text-[11px] text-stone-400">{item.subtitle}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0 text-stone-400">
                      {isSelected && (
                        <span className="flex items-center gap-1 text-[10px] text-amber-700 font-normal">
                          <span>Enter</span>
                          <ArrowRight size={10} />
                        </span>
                      )}
                    </div>
                  </div>
                </Fragment>
              );
            })
          )}
        </div>

        {/* Footer shortcuts */}
        <div className="flex items-center justify-between border-t border-stone-200/80 bg-stone-50/80 px-4 py-2 text-[11px] text-stone-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="rounded bg-white border border-stone-200 px-1 py-0.2 font-mono text-[9px] text-stone-600">
                ↑↓
              </kbd>{' '}
              {t('crm.command_menu.navigate')}
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded bg-white border border-stone-200 px-1 py-0.2 font-mono text-[9px] text-stone-600">
                ↵
              </kbd>{' '}
              {t('crm.command_menu.select')}
            </span>
          </div>
          <span className="font-mono text-[10px] text-stone-400">GovardOS Command</span>
        </div>
      </div>
    </Modal>
  );
}
